'use client';
import { useCallback, useEffect, useState } from 'react';
import { Calendar as CalendarBlock } from 'calendar-blocks';
import Month from './Month';
import { Village, VillageFutureBooking } from '@/services/types';
import { cn } from '@/lib/utils';
import useVillage from '@/hooks/useVillage';
import moment from 'moment';
import useDatePickerContext from '@/hooks/useDatePickerContext';
import GlobalLoader from '../GlobalLoader';
import { getVillageBookingDates } from '@/actions/villages';
import useVillageContext from '@/hooks/useVillageContext';
import useVillageDates from '@/hooks/useVillageDates';
import { DatePickerExtraDicts } from './Content';

export type CalendarProps = {
  village?: Village;
  shouldFetchVillageBookingDates?: boolean;
  customFutureBookings?: VillageFutureBooking[];
  confirmedDates: { start: Date | null; end: Date | null };
  rangeValue: { start: Date | null; end: Date | null };
  onRangeValueChange: (value: { start: Date | null; end: Date | null }) => void;
  lang: string;
  isOneDate?: boolean;
  inVillaRange?: { start: Date; end: Date } | null;
  atPricePreview?: boolean;
  totalExperienceRange?: { start: Date; end: Date } | null;
  extraDicts?: DatePickerExtraDicts;
  skipCheckInDaysValidation?: boolean;
};

const now = new Date();

export default function BookingCalendar({
  village,
  shouldFetchVillageBookingDates,
  customFutureBookings,
  confirmedDates,
  rangeValue,
  onRangeValueChange,
  lang,
  isOneDate = false,
  inVillaRange,
  atPricePreview,
  totalExperienceRange,
  extraDicts,
  skipCheckInDaysValidation,
}: CalendarProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [limitDate, setLimitDate] = useState<Date>();
  const [viewInfo, setViewInfo] = useState<{
    month: number;
    year: number;
  }>({
    month: confirmedDates?.end ? confirmedDates.end.getMonth() : now.getMonth(),
    year: confirmedDates?.end
      ? confirmedDates.end.getFullYear()
      : now.getFullYear(),
  });

  const { futureBookings, setFutureBookings } = useVillageContext();

  const { getUnavailableDates, getDatePickerLastAvailableDate } = useVillage(
    {},
  );

  const { getDateInfos } = useVillageDates();

  const {
    handleCalendarErrorType,
    clearCalendarErrorTypes,
    setCheckInDays,
    checkInDays,
  } = useDatePickerContext();

  const sixDaysAfterNow = moment(now).add(6, 'days').toDate();

  const getDateEnabled = useCallback(
    (date: Date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0);

      if (inVillaRange) {
        const startTime = inVillaRange.start.valueOf() - 86400000;
        const endTime = inVillaRange.end.valueOf();

        return date.getTime() >= startTime && date.getTime() <= endTime;
      }

      if (totalExperienceRange) {
        const changeStartDateMoment = moment(
          totalExperienceRange.start,
        ).startOf('day');
        const changeEndDateMoment = moment(totalExperienceRange.end).endOf(
          'day',
        );

        if (
          currentDate < changeStartDateMoment.toDate() ||
          currentDate > changeEndDateMoment.toDate()
        ) {
          return false;
        }
      }

      if (village) {
        const lastDate = getDatePickerLastAvailableDate(village.priceVariants);
        const formattedLastDate = lastDate
          ? moment(lastDate).add(5, 'hours')
          : null;

        const dateInfos = getDateInfos({
          date,
          priceVariants: village.priceVariants,
          futureBookings,
        });

        const { busy, optioned } = getUnavailableDates(futureBookings);

        const disabledDateRanges: {
          startDate: moment.Moment;
          endDate: moment.Moment;
        }[] = [];

        busy.map((booking) => {
          disabledDateRanges.push({
            startDate: booking.timestampStart,
            endDate: booking.timestampEnd,
          });
        });
        optioned.map((booking) => {
          disabledDateRanges.push({
            startDate: booking.timestampStart,
            endDate: booking.timestampEnd,
          });
        });

        const isDateWithinDisabledRanges = disabledDateRanges.some((range) => {
          const startTime = range.startDate.valueOf();
          const endTime = range.endDate.valueOf();

          return date.getTime() >= startTime && date.getTime() <= endTime;
        });

        if (
          dateInfos.isBeforeInvalidPeriod &&
          (rangeValue.start == null ||
            (rangeValue.start != null && rangeValue.end != null))
        ) {
          return false;
        }

        if (isDateWithinDisabledRanges) {
          return false;
        }

        if (
          isDateWithinDisabledRanges &&
          (dateInfos.isAfterInvalidPeriod || dateInfos.isBeforeInvalidPeriod)
        ) {
          return false;
        }

        if (formattedLastDate && date.getTime() > formattedLastDate.valueOf()) {
          return false;
        }

        if (limitDate && date.getTime() > limitDate.getTime()) {
          return false;
        }

        if (!skipCheckInDaysValidation) {
          if (
            !dateInfos.canCheckIn &&
            (rangeValue.start == null ||
              (rangeValue.start != null && rangeValue.end != null))
          ) {
            return false;
          }
        }
      }

      if (rangeValue.start && !rangeValue.end) {
        return currentDate >= rangeValue.start;
      }

      return village ? currentDate >= sixDaysAfterNow : currentDate >= today;
    },
    //eslint-disable-next-line
    [rangeValue.start, rangeValue.end, inVillaRange, futureBookings, limitDate],
  );

  function handleOnRangeChange(dates: {
    start: Date | null;
    end: Date | null;
  }) {
    if (limitDate && village) {
      setLimitDate(undefined);
    }

    if (village) {
      if (checkInDays && checkInDays.length > 0) {
        setCheckInDays([]);
        clearCalendarErrorTypes([
          'selectNextAvailableDay',
          'selectOneOfTwoAvailableDay',
          'selectOneOfAvailableDays',
        ]);
      }
    }

    onRangeValueChange(dates);
  }

  const onMonthChange = useCallback(
    ({ month: newMonth, year: newYear }: { month: number; year: number }) => {
      if (newMonth === viewInfo.month + 1 && newYear === viewInfo.year) {
        return;
      }

      setViewInfo({
        month: newMonth,
        year: newYear,
      });
    },
    [setViewInfo, viewInfo.month, viewInfo.year],
  );

  useEffect(() => {
    if (totalExperienceRange) {
      setViewInfo({
        month: totalExperienceRange.start.getMonth(),
        year: totalExperienceRange.start.getFullYear(),
      });
    }
    if (inVillaRange) {
      setViewInfo({
        month: inVillaRange.start.getMonth(),
        year: inVillaRange.start.getFullYear(),
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    async function handleGetVillageBookingDates() {
      setIsLoading(true);
      try {
        const bookingDates = await getVillageBookingDates(village?.id || 0);
        setFutureBookings(bookingDates.futureBookings);
      } catch (error) {
        setFutureBookings(village?.futureBookings || []);
      } finally {
        setIsLoading(false);
      }
    }

    const datesNotConfirmed =
      confirmedDates?.start == null && confirmedDates?.end == null;

    const shouldFetch = village && datesNotConfirmed;

    if (shouldFetch && shouldFetchVillageBookingDates) {
      handleGetVillageBookingDates();
    }
  }, [shouldFetchVillageBookingDates]);

  useEffect(() => {
    const atAvailabilitySection = atPricePreview && customFutureBookings;
    if (atAvailabilitySection && customFutureBookings.length > 0) {
      setFutureBookings(customFutureBookings);
    }
  }, [customFutureBookings]);

  return (
    <div
      className={cn(
        'w-[290px] desktop:max-w-[800px] flex flex-col my-[20px] mx-auto desktop:w-full',
        {
          'desktop:w-full': village,
        },
      )}>
      <GlobalLoader
        className="absolute"
        isLoading={isLoading && !atPricePreview}
      />

      <CalendarBlock
        className={cn(
          'flex flex-col items-center desktop:flex-row desktop:justify-around [@media(min-width:716px)]:gap-[3rem] mb-[20px] desktop:items-start',
          {
            ' desktop:max-w-[800px] desktop:flex-row items-center desktop:items-start':
              village,
          },
          {
            'desktop:h-[350px] desktop:mb-0': !atPricePreview,
          },
          {
            'desktop:flex-col desktop:items-center [@media(min-width:1581px)]:items-start [@media(min-width:1581px)]:flex-row':
              atPricePreview,
          },
        )}
        displayMonth={viewInfo.month}
        displayYear={viewInfo.year}
        onDisplayChange={onMonthChange}
        rangeValue={rangeValue}
        weekStartsOn={1}
        onRangeChange={handleOnRangeChange}
        getDateEnabled={getDateEnabled}>
        {isOneDate ? (
          <Month
            currentDate={now}
            viewInfo={viewInfo}
            setViewInfo={setViewInfo}
            rangeValue={rangeValue}
            onRangeValueChange={onRangeValueChange}
            lang={lang}
            isOneDate
            extraDicts={extraDicts}
            skipCheckInDaysValidation={skipCheckInDaysValidation}
          />
        ) : (
          <>
            <Month
              currentDate={village ? sixDaysAfterNow : now}
              futureBookings={futureBookings}
              viewInfo={viewInfo}
              setViewInfo={setViewInfo}
              rangeValue={rangeValue}
              onRangeValueChange={onRangeValueChange}
              lang={lang}
              priceVariants={village?.priceVariants}
              limitDate={limitDate}
              setLimitDate={setLimitDate}
              extraDicts={extraDicts}
              skipCheckInDaysValidation={skipCheckInDaysValidation}
            />
            <Month
              currentDate={village ? sixDaysAfterNow : now}
              futureBookings={futureBookings}
              viewInfo={{ ...viewInfo, month: viewInfo.month + 1 }}
              setViewInfo={setViewInfo}
              rangeValue={rangeValue}
              onRangeValueChange={onRangeValueChange}
              lang={lang}
              isNextMonth
              priceVariants={village?.priceVariants}
              limitDate={limitDate}
              setLimitDate={setLimitDate}
              extraDicts={extraDicts}
              skipCheckInDaysValidation={skipCheckInDaysValidation}
            />
          </>
        )}
      </CalendarBlock>
    </div>
  );
}
